import { Box, Grid, FormControl, Select, MenuItem, TextField, Typography, IconButton, InputAdornment, Chip } from "@mui/material";
import { Delete as DeleteIcon, DirectionsCar, Numbers, AttachMoney } from "@mui/icons-material";
import { IVehicleTypeResponse } from "../types";
import { newMoney } from "utils/newMoney";

interface CostLineCardProps {
  item: any;
  itemIndex: number;
  errors: any;
  loading: boolean;
  isEditing: boolean;
  onChange: (itemIndex: number, field: string, value: any) => void;
  onRemove: (itemIndex: number) => void;
  getAvailableVehicleTypes: (itemIndex: number) => IVehicleTypeResponse[];
  disableRemove?: boolean;
}

export const CostLineCard = ({
  item,
  itemIndex,
  errors,
  loading,
  isEditing,
  onChange,
  onRemove,
  getAvailableVehicleTypes,
  disableRemove
}: CostLineCardProps) => {
  return (
    <Box
      sx={{
        position: 'relative',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        borderRadius: 2,
        p: 2,
        mb: 2,
        background: '#fafbfc',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }
      }}
    >
      {/* Badge com número do veículo */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Chip
          label={`Veículo ${itemIndex + 1}`}
          size="small"
          sx={{
            background: '#89BD23',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-label': {
              px: 1.5
            }
          }}
        />
      </Box>
      {/* Botão de remover no canto superior direito */}
      {isEditing && (
        <IconButton
          color="error"
          onClick={() => onRemove(itemIndex)}
          disabled={loading || disableRemove}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            opacity: disableRemove ? 0.3 : 0.7,
            transition: 'opacity 0.2s',
            '&:hover': { 
              opacity: disableRemove ? 0.3 : 1, 
              background: '#ffeaea' 
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}

      <Grid container spacing={2}>
        {/* Primeira linha: Informações do Veículo */}
        <Grid item xs={12} sm={12} md={12}>
          <Grid container spacing={2}>
            {/* Modelo do Veículo */}
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth error={errors?.vehicleTypeId} size="medium">
                <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <DirectionsCar fontSize="small" sx={{ mr: 0.5 }} /> Modelo do Veículo
                </Typography>
                <Select
                  value={item.vehicleTypeId?.toString?.() ?? item.vehicleType?.id?.toString?.() ?? ''}
                  onChange={e => onChange(itemIndex, 'vehicleTypeId', e.target.value)}
                  disabled={loading || !isEditing}
                  size="medium"
                  sx={{ borderRadius: 2, background: '#fff' }}
                >
                  {getAvailableVehicleTypes(itemIndex).map(type => (
                    <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Capacidade Inicial */}
            <Grid item xs={6} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Numbers fontSize="small" sx={{ mr: 0.5 }} /> Capacidade Inicial
              </Typography>
              <TextField
                type="number"
                value={item.initialCapacity}
                onChange={e => onChange(itemIndex, 'initialCapacity', e.target.value)}
                fullWidth
                disabled={loading || !isEditing}
                error={errors?.initialCapacity}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff' }}
                InputProps={{ inputProps: { min: 0 }, sx: { minHeight: 48 } }}
              />
            </Grid>
            {/* Capacidade Final */}
            <Grid item xs={6} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Numbers fontSize="small" sx={{ mr: 0.5 }} /> Capacidade Final
              </Typography>
              <TextField
                type="number"
                value={item.finalCapacity}
                onChange={e => onChange(itemIndex, 'finalCapacity', e.target.value)}
                fullWidth
                disabled={loading || !isEditing}
                error={errors?.finalCapacity}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff' }}
                InputProps={{ inputProps: { min: 1 }, sx: { minHeight: 48 } }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Divisor visual */}
        <Grid item xs={12}>
          <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />
        </Grid>

        {/* Segunda linha: Valores Financeiros */}
        <Grid item xs={12} sm={12} md={12}>
          <Grid container spacing={2}>
            {/* Comissão do Líder */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AttachMoney fontSize="small" sx={{ mr: 0.5 }} /> Comissão do Líder
              </Typography>
              <TextField
                value={newMoney.apply(item.leaderCommission?.toString() || '0')}
                onChange={(e) => onChange(itemIndex, 'leaderCommission', newMoney.clean(e.target.value))}
                fullWidth
                disabled={loading || !isEditing}
                error={errors?.leaderCommission}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff', textAlign: 'right' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  sx: {
                    minHeight: 48,
                    textAlign: 'right',
                    background: (theme) => (loading || !isEditing ? '#FAFAFA !important' : '#fff')
                  }
                }}
              />
            </Grid>
            {/* Valor */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AttachMoney fontSize="small" sx={{ mr: 0.5 }} /> Valor da Rota
              </Typography>
              <TextField
                value={newMoney.apply(item.cost.toString())}
                onChange={(e) => onChange(itemIndex, 'cost', newMoney.clean(e.target.value))}
                fullWidth
                disabled={loading || !isEditing}
                error={errors?.cost}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff', textAlign: 'right' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  sx: {
                    minHeight: 48,
                    textAlign: 'right',
                    background: (theme) => (loading || !isEditing ? '#FAFAFA !important' : '#fff')
                  }
                }}
              />
            </Grid>
            {/* Adicional por Entrega */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AttachMoney fontSize="small" sx={{ mr: 0.5 }} /> Adicional por Entrega
              </Typography>
              <TextField
                value={newMoney.apply((item.additionalDelivery || 0).toString())}
                onChange={(e) => onChange(itemIndex, 'additionalDelivery', newMoney.clean(e.target.value))}
                fullWidth
                disabled={loading || !isEditing}
                error={errors?.additionalDelivery}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff', textAlign: 'right' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  sx: {
                    minHeight: 48,
                    textAlign: 'right',
                    background: (theme) => (loading || !isEditing ? '#FAFAFA !important' : '#fff')
                  }
                }}
              />
            </Grid>
            {/* Valor Descarregamento */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AttachMoney fontSize="small" sx={{ mr: 0.5 }} /> Valor Descarregamento
              </Typography>
              <TextField
                value={newMoney.apply((item.unloadingCost ?? 0).toString())}
                onChange={(e) => {
                  const val = e.target.value;
                  const cleaned = newMoney.clean(val);
                  onChange(itemIndex, 'unloadingCost', val === '' ? null : cleaned);
                }}
                fullWidth
                disabled={loading || !isEditing}
                size="medium"
                sx={{ borderRadius: 2, background: '#fff', textAlign: 'right' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  sx: {
                    minHeight: 48,
                    textAlign: 'right',
                    background: (theme) => (loading || !isEditing ? '#FAFAFA !important' : '#fff')
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}; 